//================================================================
//  Page: ApprovalsM
//================================================================

//  Purpose: My Approvals page (Mobile Version) - shows all the approvals for the current user

//================================================================

//Libraries
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import StatusLabel from '../../../Components/StatusLabel/StatusLabel';
import TabViewM from '../../../Components/TabViewM/TabViewM';

//Functions
import QueryListener from '../../../Library/QueryListener';
import ConvertDate from '../../../Library/ConvertDate';

//Images
import ArrowIcon from '../../../Components/Images/Icon_LeftArrow_Black.svg';
import IconSearch from '../../../Components/Images/Icon_Search_Grey.svg';

//CSS

export default function ApprovalsM() {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const navigate = useNavigate();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to determine status of the page > 'pending', 'onload', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');

    // Sets data for Pending tab
    const [pendingRequests, setPendingRequests] = useState([]);
    const [pendingRequestsFiltered, setPendingRequestsFiltered] = useState([]);


    // Sets data for History tab
    const [requestHistory, setRequestHistory] = useState([]);
    const [requestHistoryFiltered, setRequestHistoryFiltered] = useState([]);

    // History tab filters > 'All', 'Approved', 'Rejected
    const [currentFilter, setCurrentFilter] = useState('All');

    // Search Input
    const [searchInput, setSearchInput] = useState('');

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Handles search function
    function handleSearch(value) {

        // Clear ALL filters
        setSearchInput(value);

        // Reset the Filter
        if (value.length === 0) {

            setPendingRequestsFiltered(pendingRequests);
            setRequestHistoryFiltered(requestHistory);

            return;

        };

        // Search Filter
        //  1. Request Id
        //  2. Task Name
        //  3. Requestor email
        //  4. Task description
        const searchResultsPending = pendingRequests?.filter((object) =>
            object?.parentrequestid?.toLowerCase().includes(value.toLowerCase()) ||
            object?.taskname?.toLowerCase().includes(value.toLowerCase()) ||
            object?.createdby?.email?.toLowerCase().includes(value.toLowerCase()) ||
            object?.taskdescription?.toLowerCase().includes(value.toLowerCase())
        );
        const searchResultsHistory = requestHistory?.filter((object) =>
            object?.parentrequestid?.toLowerCase().includes(value.toLowerCase()) ||
            object?.taskname?.toLowerCase().includes(value.toLowerCase()) ||
            object?.createdby?.email?.toLowerCase().includes(value.toLowerCase()) ||
            object?.taskdescription?.toLowerCase().includes(value.toLowerCase())
        );

        // Save the filtered search results to the useState
        setPendingRequestsFiltered(searchResultsPending);
        setRequestHistoryFiltered(searchResultsHistory);

        return;
    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Get all requests
    useEffect(() => {

        function onLoadChange(documents) {

            // Get all pending requests
            const pending = documents.filter((request) => request.status === 'In Progress' || (request.status === 'For Review' && request?.requestorigin !== 'Demand'));
            const history = documents.filter((request) =>
                currentFilter !== 'All' ? request.status === currentFilter : (request.status !== 'In Progress' || (request.status === 'For Review' && request?.requestorigin !== 'Demand'))
            );

            setPendingRequests(pending);
            setRequestHistory(history);

            // Apply search filter if searchInput has a value
            if (searchInput.length > 0) {
                setPendingRequestsFiltered(
                    pending.filter((object) =>
                        object?.parentrequestid?.toLowerCase().includes(searchInput.toLowerCase()) ||
                        object?.taskname?.toLowerCase().includes(searchInput.toLowerCase()) ||
                        object?.createdby?.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
                        object?.taskdescription?.toLowerCase().includes(searchInput.toLowerCase())
                    )
                );

                setRequestHistoryFiltered(
                    history.filter((object) =>
                        object?.parentrequestid?.toLowerCase().includes(searchInput.toLowerCase()) ||
                        object?.taskname?.toLowerCase().includes(searchInput.toLowerCase()) ||
                        object?.createdby?.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
                        object?.taskdescription?.toLowerCase().includes(searchInput.toLowerCase())
                    )
                );
            } else {
                // Reset filters if no search input
                setPendingRequestsFiltered(pending);
                setRequestHistoryFiltered(history);
            }

            setPageStatus('onload');
        }

        function onError(error) {

            console.log(error);
            setPageStatus('error-fatal');

        }

        const unsubscribe = QueryListener('requests', [
            ['assignedto.email', '==', getUser.emailaddress],
            ['tasktype', 'in', ['Initial Approval', 'Commercial Second Round Approval', 'Other Approval', 'Finance Approval', 'LoA Approval', 'COO Approval', 'Final Approval', 'Quote Review', 'Budget Owner (LoA) Approval', 'Finance Manager Approval']]
        ], onLoadChange, onLoadChange, onError, ['lastmodifieddate', 'desc']);

        return () => {
            unsubscribe();
        }

        // eslint-disable-next-line
    }, [currentFilter])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (

        <div className='flex flex-col w-full'>

            <PageComponent
                status={pageStatus}
                header={
                    <div className='flex flex-col'>
                        <div className='flex flex-row bg-[#E3F1FB] w-full p-3 items-center'>
                            <img className='h-6' src={ArrowIcon} alt='Back' onClick={() => navigate('/account')}></img>
                            <h4 className='flex items-center h-full cursor-default m-auto text-[22px]'>
                                My Approvals
                            </h4>
                        </div>

                        <div className='flex flex-row bg-neutral-100 h-16 w-full px-4 py-2 overflow-hidden' hidden>

                            {/* KC TO DO - Confirm with Ellycia what this does */}
                            <select className='Input-Field-Select w-full'>
                                <option>My Approvals</option>
                            </select>

                        </div>
                    </div>
                }
                body={
                    <div className='flex flex-col'>

                        {/* ======== Search Bar ======== */}
                        <div className='flex items-center gap-[15px] bg-white px-[5px] mx-[18px] mt-[21px] mb-2 rounded-[5px] border-1 border-solid border-[#dee2e6]'>
                            <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                            <label htmlFor='searchInput'>
                                <input
                                    id='searchInput'
                                    className='border-none h-[37px] p-0 m-0 outline-none bg-white'
                                    type='text'
                                    placeholder='Search'
                                    onChange={(e) => handleSearch(e.target.value)}
                                    value={searchInput}
                                    autoComplete='off'
                                ></input>
                            </label>
                        </div>

                        <TabViewM
                            defaultView={1}
                            tabs={[
                                {
                                    id: 1,
                                    title: 'Pending',
                                    disabled: false,
                                    tag: pendingRequests?.length,
                                    content: <div className='flex flex-col gap-[20px]'>

                                        {
                                            // No approvals found
                                            pendingRequestsFiltered?.length === 0 ?
                                                (
                                                    <p className='mb-0 p-2'>
                                                        {pendingRequests?.length > 0 ? 'No approvals found.' : 'You have no pending approvals.'}
                                                    </p>
                                                ) 
                                                : 
                                                (
                                                    // Approval cards
                                                    pendingRequestsFiltered?.map((request, index) => (
                                                        <div key={index} className='shadow py-3 rounded-md flex flex-col'>

                                                            {/* Short Description */}
                                                            <div className='flex flex-row justify-between px-3'>
                                                                <label className='font-semibold'>Short Description</label>
                                                                <StatusLabel status={request?.status} tasktype={request?.tasktype}></StatusLabel>
                                                            </div>
                                                            <p className='line-clamp-2 break-words text-ellipsis max-h-[3em] px-3 pb-3 mb-3'>
                                                                {request?.taskdescription}
                                                            </p>

                                                            <hr className='p-0 m-0'></hr>

                                                            {/* Request ID */}
                                                            <div className='flex flex-row justify-between py-2 px-3 text-left'>
                                                                <label className='font-semibold'>Request ID</label>
                                                                <p className='mb-0'>
                                                                    {request?.tasktype === 'Quote Review' ? request?.requestid?.toUpperCase() : request?.parentrequestid?.toUpperCase()}
                                                                </p>
                                                            </div>

                                                            <hr className='p-0 m-0'></hr>

                                                            {/* Task Name */}
                                                            <div className='flex flex-row justify-between py-2 px-3 text-left'>
                                                                <label className='font-semibold'>Name</label>
                                                                <p className='mb-0 max-w-[75%] whitespace-nowrap overflow-hidden text-ellipsis'>
                                                                    {request?.taskname}
                                                                </p>
                                                            </div>

                                                            <hr className='p-0 m-0'></hr>

                                                            {/* Created Date */}
                                                            <div className='flex flex-row justify-between py-2 px-3 text-left'>
                                                                <label className='font-semibold'>Created</label>
                                                                {/* Get just the date */}
                                                                <p className='mb-0'>{ConvertDate(request?.createddate).split(',')[0]}</p>
                                                            </div>

                                                            {/* Navigate to Approval Details Page */}
                                                            <button className='Secondary-Button mx-3 mt-3 mb-0' onClick={() => navigate(`/approvals/${request?.requestid}`)}> View Details</button>

                                                        </div>
                                                    ))
                                                )
                                        }

                                    </div>
                                },
                                {
                                    id: 2,
                                    title: 'History',
                                    disabled: false,
                                    content: <div className='flex flex-col gap-[20px]'>

                                        {/* ======== Status (Buttons) ========== */}
                                        <div className='flex flex-row items-center gap-2 h-2 my-[10px]'>

                                            {/* All */}
                                            <button className={currentFilter === 'All' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('All')}>
                                                All
                                            </button>

                                            {/* Approved */}
                                            <button className={currentFilter === 'Completed' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Completed')}>
                                                Approved
                                            </button>

                                            {/* Rejected */}
                                            <button className={currentFilter === 'Rejected' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Rejected')}>
                                                Rejected
                                            </button>

                                            {/* Cancelled */}
                                            <button className={currentFilter === 'Cancelled' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Cancelled')}>
                                                Cancelled
                                            </button>

                                        </div>

                                        {
                                            // No approvals found
                                            requestHistoryFiltered?.length === 0 ?
                                                (
                                                    <p>
                                                        No approvals found.
                                                    </p>
                                                )
                                                :
                                                (
                                                    // Approval cards
                                                    requestHistoryFiltered?.map((request, index) => (
                                                        <div key={index} className='flex flex-col gap-0 py-3 shadow rounded-md'>

                                                            {/* Short Description */}
                                                            <div className='flex flex-row justify-between px-3'>
                                                                <label className='font-semibold'>Short Description</label>
                                                                <StatusLabel status={request?.status} tasktype={request?.tasktype}></StatusLabel>
                                                            </div>
                                                            <p className='line-clamp-2 break-words text-ellipsis max-h-[3em] px-3 pb-3 mb-3'>
                                                                {request?.taskdescription}
                                                            </p>

                                                            <hr className='p-0 m-0'></hr>

                                                            {/* Request ID */}
                                                            <div className='flex flex-row justify-between py-2 px-3 text-left'>
                                                                <label className='font-semibold'>Request ID</label>
                                                                <p className='mb-0'>
                                                                    {request?.tasktype === 'Quote Review' ? request?.requestid?.toUpperCase() : request?.parentrequestid?.toUpperCase()}
                                                                </p>
                                                            </div>

                                                            <hr className='p-0 m-0'></hr>

                                                            {/* Task Name */}
                                                            <div className='flex flex-row justify-between py-2 px-3 text-left'>
                                                                <label className='font-semibold'>Name</label>
                                                                <p className='mb-0 max-w-[75%] whitespace-nowrap overflow-hidden text-ellipsis'>
                                                                    {request?.taskname}
                                                                </p>
                                                            </div>

                                                            <hr className='p-0 m-0'></hr>

                                                            {/* Created Date */}
                                                            <div className='flex flex-row justify-between py-2 px-3 text-left'>
                                                                <label className='font-semibold'>Created</label>
                                                                {/* Get just the date */}
                                                                <p className='mb-0'>{ConvertDate(request?.createddate).split(',')[0]}</p>
                                                            </div>

                                                            {/* Navigate to Approval Details Page */}
                                                            <button className='Secondary-Button mx-3 mt-3 mb-0' onClick={() => navigate(`/approvals/${request?.requestid}`)}> View Details</button>

                                                        </div>
                                                    ))
                                                )
                                        }

                                    </div>
                                }
                            ]}
                        >
                        </TabViewM>
                    </div>}
            ></PageComponent>

        </div>
    )
}